import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { buildClassName } from 'utils/build-class-name';
import MenuButton from 'components/header/menu-button';
import styles from './style.module.scss';
import { headerIds, testIds } from 'constants/test-constants';
import Button from 'components/control/button';
import { useIsMobile } from 'hooks/use-size-class';
import UserIcon from 'components/icon/user-icon';
import { expCommercialTenant } from '@tenants';
import { LOCALE_EXP_COMMERCIAL_PATHS } from 'types/locales';
import { useTranslation } from 'next-i18next';
import ExpCommercialIcon from 'components/icon/exp-commercial-icon';
import { usePathname } from 'next/navigation';

//#region Types
type CommercialHeaderProps = {
  showHeader?: boolean;
};

type MenuItem = {
  id: string;
  label: string;
  link?: string;
  newTab?: boolean;
};

type MenuItemsProps = {
  menuItems: readonly MenuItem[];
};


const Dropdown = dynamic(() => import('components/dropdown'), {
  loading: () => <div>Loading...</div>,
  ssr: false,
});

const MenuItems = ({ menuItems }: MenuItemsProps) => {
  const { t } = useTranslation();

  return (
    <>
      {menuItems.map((menuItem: MenuItem, index: number) => (
        <a key={`${menuItem.id}-${index}`} href={menuItem.link} className={styles['menu-item']} target={menuItem.newTab ? '_blank' : '_self'} rel={menuItem.newTab ? 'noreferrer' : undefined}>
          {t(menuItem.label)}
        </a>
      ))}
    </>
  );
};

const DropdownTranslation = ({ menuItems }: MenuItemsProps) => {
  const { t } = useTranslation();

  const items = menuItems.map((menuItem: MenuItem) => ({
    ...menuItem,
    label: t(menuItem.label),
  }));

  return <Dropdown items={items} />;
};

const CommercialHeader = ({ showHeader = true }: CommercialHeaderProps) => {
  const tenant = expCommercialTenant;
  let menuItems = tenant.config?.header?.menuItems || [];
  const [menuDropdownIsActive, setMenuDropdownIsActive] = useState(false);
  const closeDropdownTimeoutRef = useRef<number | null>(null);
  const pathname = usePathname();

  // TODO: remove this when we go live with expCommercial
  let logoLink = '/';
  if (pathname === '/experts' && menuItems.length > 0) {
    logoLink = 'https://www.expcommercial.com';
    menuItems = [...menuItems.slice(0, 3),
      {
        id: 'people',
        label: LOCALE_EXP_COMMERCIAL_PATHS.HEADER_MENULINKS_PEOPLE,
        link: '/',
      },
      ...menuItems.slice(3)];
    Object.assign(menuItems[0], { link: '/become-an-advisor' });
    Object.assign(menuItems[1], { link: '/services' });
    Object.assign(menuItems[2], { link: '/experts' });
    Object.assign(menuItems[4], { link: '/about' });
  }

  useEffect(() => {
    const timeoutRef = closeDropdownTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  const toggleMenuDropdown = () => {
    setMenuDropdownIsActive(prev => !prev);
  };

  const isMobile = useIsMobile();

  return (
    <div className={buildClassName(styles.component, !showHeader && styles['hide-header'])} data-testid={headerIds.header}>
      <div className={buildClassName(styles['layout-container'])}>
        <a href={logoLink} rel="noreferrer"><ExpCommercialIcon /></a>
        <div className={styles.navigation}>
          <MenuItems menuItems={menuItems} />
          <Button
            label={!isMobile ? 'Advisor Sign In' : ''}
            Icon={isMobile && UserIcon} theme={'secondary'}
            className={styles['advisory-button']}
            onClick={() => window.open('https://www.expcommercial.com/advisor-center', '_blank')}
          />
          <MenuButton className={styles.menu} testId={testIds.menuButton} onClick={toggleMenuDropdown} closeMenu={() => setMenuDropdownIsActive(false)}>
            <>
              <div className={styles['bar-container']} id="menu">
                <span className={buildClassName(styles.bar, menuDropdownIsActive && styles.x)} />
                <span className={buildClassName(styles.bar, menuDropdownIsActive && styles.x)} />
              </div>
              {menuDropdownIsActive && <DropdownTranslation menuItems={menuItems} />}
            </>
          </MenuButton>
        </div>
      </div>
    </div>
  );
};

export default CommercialHeader;
